import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  FileUploadInput,
  TextField,
  FormField,
  ToggleButton,
  FlexColumn,
  formFieldRequiredLabel as requiredLabel,
  Link,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BigQueryAuthMethod } from 'shared/types';
import {
  BIGQUERY_SERVICE_ACCOUNT_DOCS_URL,
  BIGQUERY_WIF_DOCS_URL,
} from './constants';
import { getBigQueryAuthMethod } from './utils';

const InstructionBoxBase = styled(Typography)`
  background-color: ${({ theme }) => theme.palette.common.gray9};
  padding: 16px;
  border-radius: 8px;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray12};
  white-space: pre;
`;

const ServiceAccountInstructionBox = () => (
  <InstructionBoxBase>
    Create a BigQuery service account. Click here to{' '}
    <Link
      href={BIGQUERY_SERVICE_ACCOUNT_DOCS_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      learn more
    </Link>
    .
  </InstructionBoxBase>
);

const WifInstructionBox = () => (
  <InstructionBoxBase>
    Set up GCP Workload Identity Federation. Click here to{' '}
    <Link
      href={BIGQUERY_WIF_DOCS_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      learn more
    </Link>
    .
  </InstructionBoxBase>
);

const BigqueryFormContent = ({ disabled }: { disabled?: boolean }) => {
  const selectedAuthMethodRef = useRef<HTMLDivElement>(null);
  const methods = useFormContext();
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = methods;

  const bigqueryAuthMethod = useWatch({ name: 'bigqueryAuthMethod' });
  const authMethod = getBigQueryAuthMethod({ bigqueryAuthMethod });

  useEffect(() => {
    selectedAuthMethodRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [authMethod]);

  return (
    <>
      <FormField
        title="Location"
        errorMessage={errors.bigqueryLocation?.message as string}
      >
        <TextField
          {...register('bigqueryLocation', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. US"
          disabled={disabled}
          size="large"
        />
      </FormField>

      <FormField title="Select authentication method">
        <ToggleButtonGroup
          fullWidth
          value={authMethod}
          onChange={(_, val: BigQueryAuthMethod | null) => {
            if (val) {
              setValue('bigqueryAuthMethod', val);
            }
          }}
          exclusive
          disabled={disabled}
        >
          <ToggleButton value={BigQueryAuthMethod.SERVICE_ACCOUNT} size="large">
            Service account
          </ToggleButton>
          <ToggleButton value={BigQueryAuthMethod.WIF} size="large">
            Workload Identity Federation
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      <FlexColumn gap="inherit" ref={selectedAuthMethodRef}>
        {authMethod === BigQueryAuthMethod.SERVICE_ACCOUNT && (
          <>
            <ServiceAccountInstructionBox />
            <FormField
              title="Service account file"
              errorMessage={errors.bigqueryAccountFile?.message as string}
            >
              <Controller
                control={control}
                name="bigqueryAccountFile"
                rules={{ required: requiredLabel }}
                render={({ field }) => (
                  <FileUploadInput
                    {...field}
                    placeholder="Upload a service account JSON key file"
                  />
                )}
              />
            </FormField>
          </>
        )}

        {authMethod === BigQueryAuthMethod.WIF && (
          <>
            <WifInstructionBox />
            <FormField
              title="WIF credential file"
              errorMessage={errors.bigqueryWifFile?.message as string}
            >
              <Controller
                control={control}
                name="bigqueryWifFile"
                rules={{ required: requiredLabel }}
                render={({ field }) => (
                  <FileUploadInput
                    {...field}
                    placeholder="Upload a WIF external account JSON file"
                  />
                )}
              />
            </FormField>
          </>
        )}
      </FlexColumn>
    </>
  );
};

export default BigqueryFormContent;
